<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

/**
 * The app-bar chrome is hidden on public routes (login, 403) so they
 * can own the full viewport. Everywhere else — as long as the user
 * is authenticated — we show the top nav.
 */
const showAppBar = computed(() => !route.meta.public && auth.isAuthenticated);

const userLabel = computed(() => auth.user?.name ?? auth.user?.email ?? 'Usuario');

const roleLabel = computed(() => (auth.user?.role === 'socio' ? 'Socio' : 'Equipo interno'));

/** Sidebar drawer state. */
const drawer = ref(true);

/**
 * Navigation items.
 *
 * Clientes and Personal were moved next to each other per partner
 * feedback — both are "people" lists and partners expect to find them
 * together. Wave C inserts Proveedores between them so the three
 * "entity" lists share a contiguous block.
 */
const navItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' },
  { title: 'Clientes', icon: 'mdi-account-group', to: '/clients' },
  { title: 'Proveedores', icon: 'mdi-truck', to: '/proveedores' },
  { title: 'Personal', icon: 'mdi-account-group', to: '/personnel' },
  { title: 'Proyectos', icon: 'mdi-folder-multiple', to: '/projects' },
  { title: 'Presupuestos', icon: 'mdi-file-document-outline', to: '/budgets' },
  { title: 'Reportes', icon: 'mdi-download', to: '/reports' },
  { title: 'Equipamiento', icon: 'mdi-toolbox-outline', to: '/equipment' },
  { title: 'Inventario', icon: 'mdi-barcode-scan', to: '/inventory' },
];

/** Finance nav items (shown under a "Finanzas" divider). */
const finanzasItems = [
  { title: 'Cobros', icon: 'mdi-cash-plus', to: '/payments' },
  { title: 'Gastos', icon: 'mdi-cash-minus', to: '/expenses' },
  { title: 'Caja', icon: 'mdi-bank', to: '/finance/cashbox' },
  {
    title: 'Cuentas por Cobrar',
    icon: 'mdi-arrow-down-bold-circle',
    to: '/finance/accounts-receivable',
  },
  { title: 'Cuentas por Pagar', icon: 'mdi-arrow-up-bold-circle', to: '/finance/accounts-payable' },
  { title: 'Facturacion', icon: 'mdi-receipt-text', to: '/invoices' },
];

/** Settings nav items (shown under a divider). */
const settingsItems = [{ title: 'Comisiones', icon: 'mdi-percent', to: '/settings/commissions' }];

/** Admin nav items (socio only). */
const adminItems = [{ title: 'Usuarios', icon: 'mdi-account-cog', to: '/users' }];

async function onLogout(): Promise<void> {
  await auth.logout();
  await router.push('/login');
}

function goHome(): void {
  void router.push('/');
}
</script>

<template>
  <v-app>
    <v-app-bar v-if="showAppBar" color="surface" flat>
      <v-app-bar-nav-icon @click="drawer = !drawer" />
      <v-app-bar-title class="pa-0">
        <!--
          Using a real <v-btn> here (instead of the previous
          role="link" + tabindex="0" div) so Space and Enter both
          activate the home link natively — screen readers announce
          it correctly and no custom keydown handler is needed.
        -->
        <v-btn variant="text" class="text-none px-0" @click="goHome">
          <img src="@/assets/stars-logo-full.png" alt="STARS" height="28" />
        </v-btn>
      </v-app-bar-title>
      <v-spacer />
      <v-menu location="bottom end" offset="8">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            data-test="user-menu-btn"
            :aria-label="`Menú de usuario (${userLabel})`"
          >
            <v-icon start>mdi-account-circle</v-icon>
            <span class="text-none">{{ userLabel }}</span>
            <v-chip
              :color="auth.isSocio ? 'primary' : 'secondary'"
              variant="tonal"
              size="x-small"
              class="ms-2"
            >
              {{ roleLabel }}
            </v-chip>
          </v-btn>
        </template>
        <v-list>
          <v-list-item data-test="logout-btn" @click="onLogout">
            <template #prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>Cerrar sesión</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Sidebar navigation drawer -->
    <v-navigation-drawer v-if="showAppBar" v-model="drawer" data-test="nav-drawer">
      <div class="d-flex justify-center pa-4">
        <img src="@/assets/stars-isotype.png" alt="STARS" height="40" />
      </div>
      <v-divider class="mb-2" />
      <v-list nav density="compact">
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="route.path === item.to || (item.to !== '/' && route.path.startsWith(item.to))"
          :data-test="`nav-${item.title.toLowerCase()}`"
          rounded="lg"
        />

        <v-divider class="my-2" />
        <v-list-subheader class="text-medium-emphasis">Finanzas</v-list-subheader>
        <v-list-item
          v-for="item in finanzasItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="route.path === item.to || route.path.startsWith(item.to)"
          :data-test="`nav-${item.title.toLowerCase()}`"
          rounded="lg"
        />

        <v-divider class="my-2" />
        <v-list-subheader class="text-medium-emphasis">Configuracion</v-list-subheader>
        <v-list-item
          v-for="item in settingsItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="route.path.startsWith(item.to)"
          :data-test="`nav-${item.title.toLowerCase()}`"
          rounded="lg"
        />

        <template v-if="auth.isSocio">
          <v-divider class="my-2" />
          <v-list-subheader class="text-medium-emphasis">Administracion</v-list-subheader>
          <v-list-item
            v-for="item in adminItems"
            :key="item.to"
            :to="item.to"
            :prepend-icon="item.icon"
            :title="item.title"
            :active="route.path.startsWith(item.to)"
            :data-test="`nav-${item.title.toLowerCase()}`"
            rounded="lg"
          />
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>
